import React from 'react';
import { createPortal } from 'react-dom';
import {
    Badge, Button, Card, CardBody, CardFooter,
    CardHeader, Col, Container, FormGroup, Row,
    UncontrolledTooltip,
} from 'reactstrap';
import moment from 'moment';
import cls from 'classnames';
import {
    faAngleDoubleRight, faCheckCircle, faClock,
    faExclamationTriangle, faInfoCircle, faPaperPlane, faRecycle, faSync, faUserCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { crewLeadLabel } from '@s20/i18n';
import { appendTimeZone, formatOffsetInTimeZone } from '@s20/common/util/date';
import { getCounty } from '@s20/common/util/location';
import { httpClient } from '@s20/services/httpClient';
import { ConfirmationStatus } from '../assignmentConfirmation/AssignmentConfirmation';
import {
    FlexBetweenRow, FlexColumnCenter, FlexColumnStart,
    FlexStartRow, FormCheckbox, FormLabel, SubHeading,
    Textarea, toasty,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes, ApplicationPermissions } from '../Common';
import { ChangeLogViewer } from '../common/ChangeLogViewer';
import { CountyLabel } from '../uscounties/CountyLabel';
import { JobConfirmationStatusOptions } from '../cancelConfirmation/CancelConfirmation';
import { Alert, Modal } from '../common/Modal';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import Slideout from '../common/Slideout';
import { ContractTimesheetResponsibilityType } from '../tenant/Tenant';
import { TipAlert } from '../common/TipAlert';
import { TimesheetStatus } from '../timesheet/Timesheet';
import { doesJobHaveCrewLead } from './Dispatch';
import { JobWorkflowNotificationListener } from './JobWorkflowNotificationListener';
import './JobWorkflowEditor.scss';

const NotificationStatus = {
    Pending: 1,
    Sent: 2,
    Delivered: 3,
    Failed: 4,
    Unsubscribed: 6,
};

const DeliveryMethod = {
    Email: 2,
    SMS: 3,
};

const deliveryMethodLabels = {
    [DeliveryMethod.Email]: 'Email Notification',
    [DeliveryMethod.SMS]: 'SMS Notification',
};

const notificationStatusDisplay = {
    [NotificationStatus.Pending]: { icon: faClock, className: 'text-muted', label: 'Pending - queued for sending' },
    [NotificationStatus.Sent]: { icon: faPaperPlane, className: 'text-muted', label: 'Sent - awaiting delivery receipt' },
    [NotificationStatus.Delivered]: { icon: faCheckCircle, className: 'text-success', label: 'Delivered' },
    [NotificationStatus.Failed]: { icon: faExclamationTriangle, className: 'text-danger', label: 'Failed' },
    [NotificationStatus.Unsubscribed]: { icon: faExclamationTriangle, className: 'text-danger', label: 'Failed - recipient unsubscribed' },
};

const EDITABLE_TIMESHEET_STATUSES = [
    TimesheetStatus.New,
    TimesheetStatus.Rejected,
    TimesheetStatus.Submitted,
    TimesheetStatus.BillingPayrollRejected,
];

export default class JobWorkflowEditor extends React.Component {
    static contextType = CommonContext;

    state = {
        notificationStatuses: {}, reOptInPending: {}, resendPending: {}, jobAssignmentCancellationTypes: [],
    };

    componentDidMount() {
        this.loadNotificationStatuses();
        this.loadCancellationTypes();
    }

    loadCancellationTypes = async () => {
        try {
            const jobAssignmentCancellationTypes = await httpClient.get(ApiRoutes.jobAssignmentCancellationType.all());
            this.setState({ jobAssignmentCancellationTypes: jobAssignmentCancellationTypes ?? [] });
        } catch {
            this.setState({ jobAssignmentCancellationTypes: [] });
        }
    };

    componentDidUpdate(prevProps) {
        if (this.props.jobDetails?.assignments !== prevProps.jobDetails?.assignments) {
            this.loadNotificationStatuses();
        }
    }

    loadNotificationStatuses = async () => {
        const { assignments } = this.props.jobDetails || {};

        const noResponseIds = [...(assignments?.staff ?? []), ...(assignments?.equipment ?? [])]
            .filter((x) => x.assignment?.confirmationStatusId === ConfirmationStatus.NoResponse && x.jobAssignmentId)
            .map((x) => x.jobAssignmentId);

        if (!noResponseIds.length) {
            this.setState({ notificationStatuses: {} });
            return;
        }

        try {
            const data = await httpClient.post(ApiRoutes.messages.assignmentDeliveryStatus(), noResponseIds);

            const notificationStatuses = {};

            (data ?? []).forEach((s) => {
                (notificationStatuses[s.jobAssignmentId] ??= []).push(s);
            });

            this.setState({ notificationStatuses });
        } catch {
            this.setState({ notificationStatuses: {} });
        }
    };

    reOptIn = async (messageRecipientId) => {
        this.setState((prev) => ({
            reOptInPending: { ...prev.reOptInPending, [messageRecipientId]: true },
        }));

        try {
            await httpClient.post(ApiRoutes.messages.reOptIn(messageRecipientId));
            toasty.success('Recipient Re-opted In', 'Future messages should deliver normally.');
            await this.loadNotificationStatuses();
        } catch (err) {
            toasty.error(
                'Unable to Re-opt In',
                err?.handledError?.message ?? 'There was a problem contacting Twilio. Please try again or contact support.',
            );
        } finally {
            this.setState((prev) => ({
                reOptInPending: { ...prev.reOptInPending, [messageRecipientId]: false },
            }));
        }
    };

    resend = async (messageRecipientId) => {
        this.setState((prev) => ({
            resendPending: { ...prev.resendPending, [messageRecipientId]: true },
        }));

        try {
            await httpClient.post(ApiRoutes.messages.resend(messageRecipientId));
            toasty.success('Message Resent', 'The SMS has been re-queued for delivery.');
            await this.loadNotificationStatuses();
        } catch (err) {
            toasty.error(
                'Unable to Resend',
                err?.handledError?.message ?? 'There was a problem re-queuing this message. Please try again or contact support.',
            );
        } finally {
            this.setState((prev) => ({
                resendPending: { ...prev.resendPending, [messageRecipientId]: false },
            }));
        }
    };

    getNotificationStatusInfo = (asn) => {
        const statuses = this.state.notificationStatuses[asn.jobAssignmentId];

        if (!statuses?.length) {
            return null;
        }

        return [...statuses]
            .sort((a, b) => b.deliveryMethod - a.deliveryMethod)
            .map((status) => {
                const display = notificationStatusDisplay[status.status];

                if (!display) {
                    return null;
                }

                const receivedOn = status.statusReceivedOn
                    ? moment.utc(status.statusReceivedOn).local().format('MMM DD, YYYY hh:mm A')
                    : null;

                const label = status.deliveryMethod === DeliveryMethod.Email && status.status === NotificationStatus.Sent
                    ? 'Sent'
                    : display.label;

                const tooltipId = `notification-status-${asn.jobAssignmentId}-${status.deliveryMethod}`;

                return (
                    <div
                        className="mt-3"
                        key={status.deliveryMethod}
                        id={tooltipId}
                    >
                        <FormLabel label={deliveryMethodLabels[status.deliveryMethod] ?? 'Notification'} />
                        <h6 className={cls('assignment-status-label', display.className)}>
                            <FontAwesomeIcon
                                icon={display.icon}
                                className="mr-1"
                            />
                            {label}
                            {receivedOn ? ` - ${receivedOn}` : ''}
                        </h6>
                        {((status.status === NotificationStatus.Failed || status.status === NotificationStatus.Unsubscribed) && status.failureReason) && (
                            <UncontrolledTooltip
                                target={tooltipId}
                            >
                                {`${status.failureReason}`}
                            </UncontrolledTooltip>
                        )}
                        {(status.status === NotificationStatus.Unsubscribed) && (
                            <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                disabled={!!this.state.reOptInPending[status.messageRecipientId]}
                                onClick={() => this.reOptIn(status.messageRecipientId)}
                            >
                                <FontAwesomeIcon
                                    icon={faUserCheck}
                                    className="mr-1"
                                />
                                {this.state.reOptInPending[status.messageRecipientId] ? 'Resubscribing...' : 'Resubscribe'}
                            </Button>
                        )}
                        {(status.status === NotificationStatus.Failed) && (
                            <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                disabled={!!this.state.resendPending[status.messageRecipientId]}
                                onClick={() => this.resend(status.messageRecipientId)}
                            >
                                <FontAwesomeIcon
                                    icon={faSync}
                                    className="mr-1"
                                />
                                {this.state.resendPending[status.messageRecipientId] ? 'Resending...' : 'Resend'}
                            </Button>
                        )}

                    </div>
                );
            });
    };

    onClose = () => {
        this.setState({
            showConfirmation: false,
            selectedAssignment: null,
            notes: '',
            applyToAll: false,
        });
        this.props.onClose();
    };

    onShowChangeLogClicked = (data) => {
        const asn = { ...data };
        const { jobDetails } = { ...this.props };
        const { job } = { ...jobDetails };
        const jobName = `${job?.name} - ${job?.startTime}`;

        Alert.show({
            size: 'lg',
            title: (
                <>
                    <FlexStartRow>
                        <Badge
                            color="secondary"
                            style={{ fontSize: '.84em' }}
                            className="p-2 mr-2"
                        >{asn.name ?? asn.description}</Badge>
                        {asn.assignment.name ?? asn.assignment.description}
                    </FlexStartRow>
                    <FlexStartRow className="mt-2 mb-2">
                        <small className="font-weight-bold text-muted">{jobName}</small>
                    </FlexStartRow>
                </>
            ),
            body: (
                <ChangeLogViewer jobAssignmentId={asn.jobAssignmentId} />
            ),
        });
    };

    onSubmit = async (data, selectedAssignment) => {
        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.jobAssignment.confirm(selectedAssignment.jobAssignmentId), data);
            toasty.success('Job assignment confirmed.');
            this.onClose();
        } catch {
            toasty.error('There was a problem confirming the assignment.  Please try your request again or contact support.');
        } finally {
            this.setState({ saving: false });
        }
    };

    getWarningMessage = () => {
        const { jobDetails } = this.props;
        const { assignments } = jobDetails || {};

        if (!assignments) {
            return null;
        }

        const { job, day } = jobDetails;

        if (
            (assignments.staff ?? []).length > 0
            && job.contractTimesheetResponsibilityTypeId === ContractTimesheetResponsibilityType.CrewLead
        ) {
            const hasCrewLead = doesJobHaveCrewLead(job, day);
            if (!hasCrewLead) {
                return `Please assign a ${crewLeadLabel(this.context.tenant.tenantSettings)} on this job for this day before editing workflow.`;
            }
        }

        // If no emps or equipment, no need to open editor.
        if (
            !(assignments.staff ?? []).length
            && !(assignments.equipment ?? []).length
        ) {
            return 'Please make an assignment to the job on this day before editing workflow.';
        }

        // Make sure no equipment is unassigned.
        // For equipment-only jobs (no staff), only require assignedEquipment, not assignedEmployee.
        const hasStaffAssignments = (assignments.staff ?? []).length > 0;
        if (
            (assignments.equipment ?? []).filter(
                (x) => !x.assignedEquipment?.id && (hasStaffAssignments ? !x.assignedEmployee?.id : false),
            ).length
        ) {
            return 'Please ensure all job equipment has been assigned on this day before editing workflow.';
        }

        return null;
    };

    getConfirmationControls = (asn, asn_inx) => {
        const crewLead = crewLeadLabel(this.context.tenant.tenantSettings);
        const currentAssignment = JobConfirmationStatusOptions.find((x) => x.value === (asn.assignment?.confirmationStatusId ?? 1));

        const hasWarning = !!this.getWarningMessage();

        return (
            <Card
                key={asn_inx}
                className="mt-2 mb-2"
            >
                <CardHeader>
                    <FlexBetweenRow>
                        <FlexStartRow>
                            <Badge
                                color="secondary"
                                className="p-2 mr-2"
                            >{asn.name ?? asn.description}</Badge>
                            {asn.assignment.name ?? asn.assignment.description}
                        </FlexStartRow>

                        {!!asn.isCrewLead && (
                            <span
                                className="text-warning"
                                title={`${crewLead}`}
                            >
                                <i className="fa fa-hard-hat fa-lg mr-2" />
                                <small>{`${crewLead.toUpperCase()}`}</small>
                            </span>
                        )}

                        {!!asn?.assignedEmployee && <small className="text-success">{`Assigned To: ${asn.assignedEmployee.name}`}</small>}
                        {!!asn?.assignedEquipment && <small className="text-success">{`Assigned To: ${asn.assignedEquipment.description}`}</small>}
                    </FlexBetweenRow>
                </CardHeader>

                <CardBody>
                    <div>
                        <FlexBetweenRow>
                            <div className="d-flex flex-column">
                                <FormLabel
                                    label="Current Status"
                                />

                                <h6 className={cls('assignment-status-label', currentAssignment.class)}>
                                    {currentAssignment.label}
                                </h6>
                                {currentAssignment.value === ConfirmationStatus.NoResponse && this.getNotificationStatusInfo(asn)}
                                {asn?.dispatchedOn && (
                                    <div className="mt-3">
                                        <FormLabel label="Dispatched On" />
                                        <h6 className="assignment-status-label text-muted">
                                            {formatOffsetInTimeZone(asn.dispatchedOn, 'MMM DD, YYYY hh:mm A', asn.timeZone || asn.assignment?.timeZone)}
                                        </h6>
                                    </div>
                                )}
                                {asn?.dispatchedByName && (
                                    <div className="mt-3">
                                        <FormLabel label="Dispatched By" />
                                        <h6 className="assignment-status-label text-muted">
                                            {asn.dispatchedByName}
                                        </h6>
                                    </div>
                                )}
                            </div>

                            {currentAssignment.value < ConfirmationStatus.Confirmed && (
                                <>
                                    <FontAwesomeIcon
                                        icon={faAngleDoubleRight}
                                        className="text-muted"
                                    />

                                    <Button
                                        color="primary"
                                        onClick={() => this.showConfirmationMessage(asn)}
                                        disabled={hasWarning}
                                    >
                                        Confirm Assignment
                                    </Button>
                                </>
                            )}
                        </FlexBetweenRow>
                    </div>
                </CardBody>

                <CardFooter>
                    <Button
                        tag="span"
                        color="link"
                        className="site-link"
                        size="sm"
                        title="View Change Log"
                        onClick={() => this.onShowChangeLogClicked({ ...asn })}
                    >
                        View Change Log
                    </Button>
                </CardFooter>
            </Card>
        );
    };

    getJobDetails = () => {
        const { usCounties } = this.props;

        const job = this.coalesceJobDetails();
        const formTitle = `${job?.name} - ${appendTimeZone(this.getJobStartTime(), job.startTimeOffset, job.timeZone)}`;
        const foreman = job?.foremanName ?? '';

        const county = getCounty(usCounties, {
            usCountyId: job.usCounty?.id,
            contractUnionWorkTypeId: job.contractUnionWorkTypeId,
        });
        return (
            <div className="table-responsive">
                <table className="table table-sm table-job-workflow-details">
                    <thead>
                        <tr>
                            <th
                                colSpan="2"
                                scope="col"
                            >
                                <FlexStartRow>
                                    <Badge
                                        color="secondary"
                                        className="mr-2"
                                        style={{ fontSize: '100% !important' }}
                                    >{job.number}</Badge>
                                    {formTitle}
                                </FlexStartRow>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th scope="row">
                                Created By
                            </th>
                            <td>{job.createdByName}</td>
                        </tr>
                        {!!job?.createdOn && (
                            <tr>
                                <th scope="row">
                                    Created On
                                </th>
                                <td>{formatOffsetInTimeZone(job.createdOn, 'MMM D, YYYY h:mm A', job.timeZone)}</td>
                            </tr>
                        )}
                        <tr>
                            <th scope="row">
                                Foreman
                            </th>
                            <td>{foreman}</td>
                        </tr>
                        {!!job?.workOrderNumber && (
                            <tr>
                                <th scope="row">
                                    Work Order Number
                                </th>
                                <td className="text-danger">{job?.workOrderNumber}</td>
                            </tr>
                        )}
                        <tr>
                            <th scope="row">
                                Meeting Location
                            </th>
                            <td>{job?.meetingAddress}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                Job Location
                            </th>
                            <td>{job?.jobAddress}</td>
                        </tr>

                        {Boolean(job?.usCounty) && (
                            <tr>
                                <th scope="row">
                                    Job County
                                </th>
                                <td>
                                    <CountyLabel {...county} />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    coalesceJobDetails = () => {
        const { jobDetails } = { ...this.props };
        const { job } = { ...jobDetails };

        const override = this.selectedDayHasOverride();

        if (!override) {
            return job;
        }

        return {
            ...job,
            startTime: override.startTime || job.startTime,
            foremanName: override.foremenName || job.foremanName,
            workOrderNumber: override.workOrderNumber || job.workOrderNumber,
            meetingAddress: override.meetingAddress || job.meetingAddress,
            jobAddress: override.jobAddress || job.jobAddress,
            usCounty: override.usCounty || job.usCounty,
        };
    };

    selectedDayHasOverride = () => {
        const { jobDetails } = this.props;
        const { job, day: dayIndex } = jobDetails;

        if (!job?.overrides || job?.overrides.length === 0) {
            return false;
        }

        return job.overrides.find((override) => moment(override.overrideDate, moment.ISO_8601).day() === dayIndex);
    };

    showConfirmationMessage = (assignment) => {
        const _toastHeader = 'Unable to Confirm This Assignment';
        const _validationToastOptions = { autoClose: false, closeOnClick: true };
        const selectedAssignment = { ...assignment };
        const { jobDetails } = { ...this.props };
        const { assignments } = { ...jobDetails };
        const isEquipmentAssignment = !!selectedAssignment.requiredEquipment.length;
        const staffConfirmations = assignments.staff.filter((x) => x.assignment?.confirmationStatusId === ConfirmationStatus.Confirmed);
        const equipmentConfirmations = assignments.equipment.filter((x) => x.assignment?.confirmationStatusId === ConfirmationStatus.Confirmed);

        if (isEquipmentAssignment) {
            // Validation #1: Don't allow confirming equipment before the responsible personnel has been confirmed at the job site.
            //               Only applies when there are staff assignments (not equipment-only jobs).
            if (selectedAssignment.assignedEmployee?.id) {
                const hasAnyStaff = (assignments.staff?.length ?? 0) > 0;
                if (hasAnyStaff) {
                    const rpIsAssigned = !!staffConfirmations.filter((x) => x.assignment?.id === selectedAssignment.assignedEmployee.id).length;
                    if (!rpIsAssigned) {
                        toasty.error(
                            _toastHeader,
                            <FlexColumnStart>
                                <div>
                                    Please ensure
                                    {' '}
                                    <strong>{`[${selectedAssignment.assignedEmployee.name}]`}</strong>
                                    {' '}
                                    has been confirmed for this job before confirming their equipment.
                                </div>
                                <div className="pt-2">
                                    <small>Click to dismiss.</small>
                                </div>
                            </FlexColumnStart>,
                            _validationToastOptions,
                        );
                        return false;
                    }
                }
            }

            // Validation #2: Don't allow confirming equipment before the parent equipment has been confirmed at the job site.
            if (selectedAssignment.assignedEquipment?.id) {
                const parentEqIsAssigned = !!equipmentConfirmations.filter((x) => x.assignment?.id === selectedAssignment.assignedEquipment.id).length;
                if (!parentEqIsAssigned) {
                    toasty.error(
                        _toastHeader,
                        <FlexColumnStart>
                            <div>
                                Please ensure
                                {' '}
                                <strong>{`[${selectedAssignment.assignedEquipment.equipmentType.description} #${selectedAssignment.assignedEquipment.description}]`}</strong>
                                {' '}
                                is confirmed for this job before confirming this equipment.
                            </div>
                            <div className="pt-2">
                                <small>Click to dismiss.</small>
                            </div>
                        </FlexColumnStart>,
                        _validationToastOptions,
                    );
                    return false;
                }
            }
        }

        const selectedAssignmentName = selectedAssignment?.assignment?.name ?? selectedAssignment?.assignment?.description;

        Modal.show({
            title: 'Set Assignment Status',
            onSubmit: (values) => this.onSubmit(values, selectedAssignment),
            SaveButtonProps: {
                label: 'Yes, confirm assignment',
                savingLabel: 'Setting assignment status',
            },
            zIndex: 'auto',
            body: () => (
                <>
                    {`Set assignment status for ${selectedAssignmentName} on this job to confirmed?`}

                    <Textarea
                        formGroupClassName="mt-3"
                        label="Notes"
                        id="notes"
                        name="notes"
                        required
                        placeholder="Detail the reason for the manual status change (500 characters max)"
                        maxLength="500"
                        rows="5"
                        error="Notes are required and must not exceed 500 characters in length."
                    />

                    <FormGroup className="mt-3">
                        <FormCheckbox
                            id="applyToAll"
                            name="applyToAll"
                            type="checkbox"
                            labelText="Apply to all Assignments"
                        />
                    </FormGroup>
                </>
            ),
        });
    };

    getJobStartTime = () => {
        const { jobDetails } = this.props;
        if (!jobDetails) {
            return '';
        }
        const override = this.selectedDayHasOverride();

        return override?.startTime ? moment(override.startTime, 'HH:mm').format('hh:mm A') : jobDetails.job.startTime;
    };

    showCancellationTypeChangeModal = (cancelled) => {
        const cancellationTypes = this.state.jobAssignmentCancellationTypes ?? [];
        const typeOptions = cancellationTypes.filter((t) => {
            if (t.id === cancelled.jobAssignmentCancellationTypeId) return false;
            return true;
        });

        Modal.show({
            title: 'Edit Cancellation Type',
            SaveButtonProps: {
                label: 'Update',
                savingLabel: 'Updating...',
            },
            body: ({ values }) => {
                const selectedTypeId = parseInt(values?.cancellationTypeId ?? 0, 10);
                const selectedType = cancellationTypes.find((t) => t.id === selectedTypeId);
                const preventsReassignment = Boolean(selectedType?.preventsReassignment);

                return (
                    <>
                        <FormGroup>
                            <small className="text-muted">
                                <span className="font-weight-bold">Current Type:</span>
                                {' '}
                                {cancelled.jobAssignmentCancellationType}
                            </small>
                        </FormGroup>

                        <ValidatedSelect
                            required
                            isClearable
                            id="cancellationTypeId"
                            name="cancellationTypeId"
                            label="New Cancellation Type"
                            placeholder="Select Cancellation Type"
                            options={typeOptions}
                            getOptionLabel={(option) => option.description}
                            getOptionValue={(option) => option.id}
                            valueField="id"
                            error="Please select a cancellation type."
                        />

                        {preventsReassignment && (
                            <div className="mb-2 text-danger">
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="mr-1"
                                />
                                Selecting this will prevent the employee from being reassigned for the remainder of the day.
                            </div>
                        )}

                        <Textarea
                            required
                            id="cancellationNote"
                            name="cancellationNote"
                            label="Notes"
                            placeholder="Enter the reason for changing the cancellation type."
                            maxLength="500"
                            rows="5"
                            error="Notes are required."
                        />
                    </>
                );
            },
            onSubmit: async (values) => {
                const newCancellationTypeId = parseInt(values.cancellationTypeId ?? 0, 10);
                const cancellationNote = (values.cancellationNote ?? '').trim();
                if (!newCancellationTypeId) {
                    toasty.error('Please select a cancellation type.');
                    return { keepOpen: true };
                }

                if (!cancellationNote) {
                    toasty.error('Notes are required.');
                    return { keepOpen: true };
                }
                const model = {
                    jobAssignmentCancellationId: cancelled.id,
                    newCancellationTypeId,
                    cancellationNote,
                    assignedEmployeeId: cancelled.assignedEmployeeId,
                };
                try {
                    await httpClient.post(ApiRoutes.dispatch.changeCancellationType(), model);

                    toasty.success('Cancellation type updated.');
                    await this.props.onCancellationTypeChanged?.();
                } catch (err) {
                    toasty.error(
                        'Unable to change cancellation type',
                        err?.handledError?.message ?? 'Please try your request again.',
                    );
                    return { keepOpen: true };
                }

                return undefined;
            },
        });
    };

    render() {
        const { jobDetails, isCancelledJob } = this.props;
        const { assignments } = jobDetails || {};

        const warning = this.getWarningMessage();
        const dispatchEditPermission = this.context.checkUserHasPermission(ApplicationPermissions.dispatch_edit);

        const jobAssignmentIds = [...(assignments?.staff ?? []), ...(assignments?.equipment ?? [])]
            .map((x) => x.jobAssignmentId)
            .filter(Boolean);

        return createPortal(
            <>
                {Boolean(jobDetails) && (
                    <Container
                        fluid
                        className="p-2"
                    >
                        <Row>
                            <Col>
                                <SubHeading className="mb-2">Day Details</SubHeading>
                                {this.getJobDetails()}

                                {!isCancelledJob && (
                                    <>
                                        <SubHeading>Assignments</SubHeading>
                                        <TipAlert
                                            label="You may manually confirm an unconfirmed job assignment here.  Please note that this override will not provide employees with notifications."
                                        />

                                        <TipAlert
                                            label="Please note that you cannot confirm equipment until at least one crew member has been confirmed for the job."
                                        />

                                        {Boolean(warning) && (
                                            <TipAlert
                                                label={warning}
                                                tipText="Warning"
                                                className="font-weight-bold"
                                                color="danger"
                                            />
                                        )}

                                        <FlexColumnCenter className="day-assignments">
                                            {assignments?.staff?.map((asn, asn_inx) => !!asn.assignment && this.getConfirmationControls(asn, asn_inx))}

                                            {assignments?.equipment?.map((asn, asn_inx) => !!asn.assignment && this.getConfirmationControls(asn, asn_inx))}
                                        </FlexColumnCenter>
                                    </>
                                )}
                                {!!(assignments?.cancelledAssignments ?? []).length && (
                                    <>
                                        <SubHeading className="mt-3">Cancelled Assignments</SubHeading>
                                        <FlexColumnCenter className="day-assignments">
                                            {assignments.cancelledAssignments.map((cancelled, idx) => {
                                                const displayName = cancelled.assignedEmployee || cancelled.assignedEquipment;
                                                return (
                                                    <Card
                                                        key={idx}
                                                        className="mt-2 mb-2"
                                                    >
                                                        <CardHeader>
                                                            <FlexBetweenRow>
                                                                <FlexStartRow>
                                                                    <Badge
                                                                        color="secondary"
                                                                        className="p-2 mr-2"
                                                                    >Cancelled</Badge>
                                                                    {displayName}
                                                                </FlexStartRow>
                                                                {!!cancelled.isCrewLead && (
                                                                    <span
                                                                        className="text-warning"
                                                                        title={crewLeadLabel(this.context.tenant.tenantSettings)}
                                                                    >
                                                                        <i className="fa fa-hard-hat fa-lg mr-2" />
                                                                        <small>{crewLeadLabel(this.context.tenant.tenantSettings, 'upper')}</small>
                                                                    </span>
                                                                )}
                                                            </FlexBetweenRow>
                                                        </CardHeader>
                                                        <CardBody>
                                                            <h6 className="assignment-status-label text-danger">Cancelled</h6>
                                                            <div className="mt-2">
                                                                {!!cancelled.dispatchedOn && (
                                                                    <div>
                                                                        <small className="text-muted font-weight-bold">Dispatched On: </small>
                                                                        <small>
                                                                            {formatOffsetInTimeZone(cancelled.dispatchedOn, 'MMM DD, YYYY hh:mm A', cancelled.timeZone || cancelled.assignment?.timeZone)}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                                {!!cancelled.dispatchedByName && (
                                                                    <div>
                                                                        <small className="text-muted font-weight-bold">Dispatched By: </small>
                                                                        <small>{cancelled.dispatchedByName}</small>
                                                                    </div>
                                                                )}
                                                                {!!cancelled.jobAssignmentCancellationType && (
                                                                    <div>
                                                                        <small className="text-muted font-weight-bold">Type: </small>
                                                                        <small>{cancelled.jobAssignmentCancellationType}</small>
                                                                    </div>
                                                                )}
                                                                {!!cancelled.cancelledByEmployee && (
                                                                    <div>
                                                                        <small className="text-muted font-weight-bold">Cancelled By: </small>
                                                                        <small>{cancelled.cancelledByEmployee}</small>
                                                                    </div>
                                                                )}
                                                                {!!cancelled.cancelledOn && (
                                                                    <div>
                                                                        <small className="text-muted font-weight-bold">Cancelled On: </small>
                                                                        <small>
                                                                            {formatOffsetInTimeZone(cancelled.cancelledOn, 'MMM DD, YYYY hh:mm A', cancelled.timeZone || cancelled.assignment?.timeZone)}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                                {!!cancelled.note && (
                                                                    <div className="mt-1">
                                                                        <small className="text-muted font-weight-bold">Note: </small>
                                                                        <small>{cancelled.note}</small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </CardBody>
                                                        {!!cancelled.jobAssignmentId && (
                                                            <CardFooter>
                                                                <Button
                                                                    tag="span"
                                                                    color="link"
                                                                    className="site-link"
                                                                    size="sm"
                                                                    title="View Change Log"
                                                                    onClick={() => this.onShowChangeLogClicked({
                                                                        jobAssignmentId: cancelled.jobAssignmentId,
                                                                        name: 'Cancelled',
                                                                        assignment: { name: displayName },
                                                                    })}
                                                                >
                                                                    View Change Log
                                                                </Button>
                                                            </CardFooter>
                                                        )}
                                                    </Card>
                                                );
                                            })}
                                        </FlexColumnCenter>
                                    </>
                                )}
                            </Col>
                        </Row>

                        <JobWorkflowNotificationListener
                            jobAssignmentIds={jobAssignmentIds}
                            onNotification={this.loadNotificationStatuses}
                        />
                    </Container>
                )}

                <Slideout
                    size="col-xl-5 col-lg-6 col-md-8 col-sm-12"
                    show={!!jobDetails}
                    loading={!assignments}
                    id="jobWorkflowEditor"
                    formIcon={faRecycle}
                    formTitle={(
                        <FlexColumnStart>
                            {!dispatchEditPermission || !assignments?.canEdit || isCancelledJob ? (
                                <span> View Job Workflow </span>
                            ) : (
                                <>
                                    <span>Edit Job Workflow</span>
                                    <small className="text-muted">Set Confirmation Status for Assignments</small>
                                </>
                            )}
                        </FlexColumnStart>
                    )}
                    className="job-workflow-editor"
                    onClose={this.onClose.bind(this)}
                >
                    {Boolean(jobDetails) && (
                        <Container
                            fluid
                            className="p-2"
                        >
                            <Row>
                                <Col>
                                    <SubHeading className="mb-2">Day Details</SubHeading>
                                    {this.getJobDetails()}

                                    {!isCancelledJob && (
                                        <>
                                            <SubHeading>Assignments</SubHeading>
                                            <TipAlert
                                                label="You may manually confirm an unconfirmed job assignment here.  Please note that this override will not provide employees with notifications."
                                            />

                                            <TipAlert
                                                label="Please note that you cannot confirm equipment until at least one crew member has been confirmed for the job."
                                            />

                                            {Boolean(warning) && (
                                                <TipAlert
                                                    label={warning}
                                                    tipText="Warning"
                                                    className="font-weight-bold"
                                                    color="danger"
                                                />
                                            )}

                                            <FlexColumnCenter className="day-assignments">
                                                {assignments?.staff?.map((asn, asn_inx) => !!asn.assignment && this.getConfirmationControls(asn, asn_inx))}

                                                {assignments?.equipment?.map((asn, asn_inx) => !!asn.assignment && this.getConfirmationControls(asn, asn_inx))}
                                            </FlexColumnCenter>
                                        </>
                                    )}
                                    {!!(assignments?.cancelledAssignments ?? []).length && (
                                        <>
                                            <SubHeading className="mt-3">Cancelled Assignments</SubHeading>
                                            <FlexColumnCenter className="day-assignments">
                                                {assignments.cancelledAssignments.map((cancelled, idx) => {
                                                    const displayName = cancelled.assignedEmployee || cancelled.assignedEquipment;
                                                    const isJobAssignmentExist = cancelled.jobAssignmentId;
                                                    return (
                                                        <Card
                                                            key={idx}
                                                            className="mt-2 mb-2"
                                                        >
                                                            <CardHeader>
                                                                <FlexBetweenRow>
                                                                    <FlexStartRow>
                                                                        <span className="badge badge-lg badge-secondary p-2 mr-2">Cancelled</span>
                                                                        {displayName}
                                                                    </FlexStartRow>
                                                                    {!!cancelled.isCrewLead && (
                                                                        <span
                                                                            className="text-warning"
                                                                            title={crewLeadLabel(this.context.tenant.tenantSettings)}
                                                                        >
                                                                            <i className="fa fa-hard-hat fa-lg mr-2" />
                                                                            <small>{crewLeadLabel(this.context.tenant.tenantSettings, 'upper')}</small>
                                                                        </span>
                                                                    )}
                                                                </FlexBetweenRow>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <FlexBetweenRow>
                                                                    <div className="d-flex flex-column">
                                                                        <h6 className="assignment-status-label text-danger">Cancelled</h6>
                                                                        <div className="mt-2">
                                                                            {!!cancelled.dispatchedOn && (
                                                                                <div>
                                                                                    <small className="text-muted font-weight-bold">Dispatched On: </small>
                                                                                    <small>
                                                                                        {formatOffsetInTimeZone(cancelled.dispatchedOn, 'MMM DD, YYYY hh:mm A', cancelled.timeZone || cancelled.assignment?.timeZone)}
                                                                                    </small>
                                                                                </div>
                                                                            )}
                                                                            {!!cancelled.dispatchedByName && (
                                                                                <div>
                                                                                    <small className="text-muted font-weight-bold">Dispatched By: </small>
                                                                                    <small>{cancelled.dispatchedByName}</small>
                                                                                </div>
                                                                            )}
                                                                            {!!cancelled.jobAssignmentCancellationType && (
                                                                                <div>
                                                                                    <small className="text-muted font-weight-bold">Type: </small>
                                                                                    <small>{cancelled.jobAssignmentCancellationType}</small>
                                                                                </div>
                                                                            )}
                                                                            {!!cancelled.cancelledByEmployee && (
                                                                                <div>
                                                                                    <small className="text-muted font-weight-bold">Cancelled By: </small>
                                                                                    <small>{cancelled.cancelledByEmployee}</small>
                                                                                </div>
                                                                            )}
                                                                            {!!cancelled.cancelledOn && (
                                                                                <div>
                                                                                    <small className="text-muted font-weight-bold">Cancelled On: </small>
                                                                                    <small>
                                                                                        {formatOffsetInTimeZone(cancelled.cancelledOn, 'MMM DD, YYYY hh:mm A', cancelled.timeZone || cancelled.assignment?.timeZone)}
                                                                                    </small>
                                                                                </div>
                                                                            )}
                                                                            {!!cancelled.note && (
                                                                                <div className="mt-1">
                                                                                    <small className="text-muted font-weight-bold">Note: </small>
                                                                                    <small>{cancelled.note}</small>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {(!cancelled.timesheetStatusId
                                                                        || EDITABLE_TIMESHEET_STATUSES.includes(cancelled.timesheetStatusId)) && isJobAssignmentExist && (
                                                                            <div className="d-flex flex-row align-items-center align-self-start">
                                                                                <FontAwesomeIcon
                                                                                    icon={faAngleDoubleRight}
                                                                                    className="text-muted"
                                                                                />
                                                                                <Button
                                                                                    color="primary"
                                                                                    className="ml-2 text-nowrap"
                                                                                    onClick={() => this.showCancellationTypeChangeModal(cancelled)}
                                                                                >
                                                                                    Edit Cancel Type
                                                                                </Button>
                                                                            </div>
                                                                        )}
                                                                </FlexBetweenRow>
                                                            </CardBody>
                                                            {!!cancelled.jobAssignmentId && (
                                                                <CardFooter>
                                                                    <Button
                                                                        tag="span"
                                                                        color="link"
                                                                        className="site-link"
                                                                        size="sm"
                                                                        title="View Change Log"
                                                                        onClick={() => this.onShowChangeLogClicked({
                                                                            jobAssignmentId: cancelled.jobAssignmentId,
                                                                            name: 'Cancelled',
                                                                            assignment: { name: displayName },
                                                                        })}
                                                                    >
                                                                        View Change Log
                                                                    </Button>
                                                                </CardFooter>
                                                            )}
                                                        </Card>
                                                    );
                                                })}
                                            </FlexColumnCenter>
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </Container>
                    )}
                </Slideout>
            </>,
            document.body,
        );
    }
}
